import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Public base for links we SEND to prospects (WhatsApp/e-mail report links).
//
// This used to be `window.location.origin` at each call site, which is right
// only when the admin panel happens to be served from the production domain.
// Running the admin locally against the production backend — the normal way
// this gets used — silently produced `http://localhost:3003/rapor/<token>`
// links, and a link like that is simply dead for the recipient. Every one of
// the 38 report links created so far points at localhost for exactly this
// reason.
//
// So: an explicit env var wins, and `window.location.origin` stays only as the
// fallback for local development where it is correct anyway.
const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "").replace(/\/+$/, "");

export const publicBaseUrl = () =>
  PUBLIC_URL || (typeof window !== "undefined" ? window.location.origin : "");

export const reportUrl = (token) => (token ? `${publicBaseUrl()}/rapor/${token}` : null);
const TOKEN_KEY = "positivup_admin_token";

export const api = axios.create({ baseURL: API });

// Kurumsal giriş (/admin) — her istekte varsa token'ı ekle. Public uç
// noktalar (report/*, public/scan, leads POST, auth/login) fazladan
// Authorization header'ını zaten görmezden geliyor, zararı yok.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Süresi dolmuş/geçersiz token → oturumu temizle ve girişe yönlendir.
// /admin dışında (public sayfalarda) hiçbir istek 401 dönmeyeceği için
// bu sadece admin akışını etkiler.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      if (window.location.pathname.startsWith("/admin")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAdminToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAdminToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export async function login(email, password) {
  const { data } = await api.post("/auth/login", { email, password });
  setAdminToken(data.access_token);
  return data;
}

export async function runSearch(payload) {
  const { data } = await api.post("/search", payload);
  return data;
}

export async function listSearches() {
  const { data } = await api.get("/searches");
  return data;
}

export async function getSearch(id) {
  const { data } = await api.get(`/searches/${id}`);
  return data;
}

export async function deleteSearch(id) {
  const { data } = await api.delete(`/searches/${id}`);
  return data;
}

export async function findEmails(id) {
  const { data } = await api.post(`/searches/${id}/find-emails`);
  return data;
}

export async function findOwners(id) {
  const { data } = await api.post(`/searches/${id}/find-owners`);
  return data;
}

export async function runAudit(id) {
  const { data } = await api.post(`/searches/${id}/audit`);
  return data;
}

export async function runReviewAnalysis(id, placeIds) {
  const { data } = await api.post(`/searches/${id}/review-analysis`, {
    place_ids: placeIds,
  });
  return data;
}

// checks: { [checkCode]: true | false } — only the keys being changed.
// Backend merges into the business's manual_gbp_review, never replaces it.
export async function updateManualGbpReview(searchId, placeId, checks) {
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/manual-gbp-review`,
    { checks }
  );
  return data;
}

// kind: "description" | "services" — pasted GBP text, analysed server-side
// (see backend/audit/gbp_text_analysis.py) instead of judged checkbox by
// checkbox. Returns { derived, applied, manual_gbp_review, business }.
export async function analyzeManualGbpText(searchId, placeId, kind, text) {
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/manual-gbp-review/analyze`,
    { kind, text }
  );
  return data;
}

// Vision-AI best-effort suggestion for logo/exterior/interior photos —
// admin still confirms with one click, see photo-suggestions endpoint.
export async function suggestPhotoCategories(searchId, placeId) {
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/photo-suggestions`
  );
  return data;
}

export async function createReportLink(searchId, placeId) {
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/report`
  );
  return data;
}

// 6x6 Google Maps grid-rank heatmap — expensive (~36x a single check), so
// this is always an explicit, separate action, never bundled automatically.
export async function createGridRank(searchId, placeId) {
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/grid-rank`
  );
  return data;
}

// Wide report (paid-tier, PRD faz 1): gather -> generate -> (edit) -> approve.
// All admin-only, decoupled from payment for now — see memory/PRD.md §7/§9.
export async function gatherWideReportData(searchId, placeId) {
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/wide-report/gather`
  );
  return data;
}

export async function generateWideReport(searchId, placeId) {
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/wide-report/generate`
  );
  return data;
}

export async function updateWideReportNarrative(searchId, placeId, narrative) {
  const { data } = await api.patch(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/wide-report/narrative`,
    narrative
  );
  return data;
}

export async function approveWideReport(searchId, placeId, force = false) {
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/wide-report/approve`,
    null,
    force ? { params: { force: true } } : undefined
  );
  return data;
}

export async function gatherContentOpportunities(searchId, placeId) {
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/wide-report/content-opportunities/gather`
  );
  return data;
}

export async function generateContentRecommendations(searchId, placeId) {
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/wide-report/content-opportunities/generate`
  );
  return data;
}

export async function updateConsent(searchId, placeId, consent) {
  const { data } = await api.patch(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/consent`,
    { consent }
  );
  return data;
}

// Public, unauthenticated — the marketing landing page's own request form
// (frontend/src/components/landing/RequestForm.jsx). A prospect requesting
// their first scan, before any search/audit has ever run for them — a
// different funnel from the admin's own bulk search, so it gets its own
// lightweight endpoint/collection rather than reusing `runSearch`. The
// admin actions these manually with the existing search/audit tools; this
// just captures the lead, it does not trigger the audit engine itself.
export async function requestScan(payload) {
  const { data } = await api.post("/leads", payload);
  return data;
}

// Public, unauthenticated — the /analyse self-serve wizard (AnalysisWizard.jsx).
// Runs a real (free-tier only) audit synchronously and returns a real report
// token — see backend/server.py POST /api/public/scan for the cost-safety
// rationale (no DataForSEO calls on this public path).
export async function startPublicScan(payload) {
  const { data } = await api.post("/public/scan", payload);
  return data;
}

// Public, unauthenticated — used by ReportPage, not the admin dashboard.
export async function fetchPublicReport(token) {
  // The admin previewing on their own browser is flagged so it is not counted
  // as a prospect opening the report.
  const { data } = await api.get(`/report/${encodeURIComponent(token)}`, {
    params: getAdminToken() ? { own: true } : undefined,
  });
  return data;
}

export async function runAiTest(token) {
  const { data } = await api.post(`/report/${encodeURIComponent(token)}/ai-test`);
  return data?.ai_test || null;   // 204 (skipped, e.g. non-browser client) has no body
}

export async function completeReport(searchId, placeId) {
  // The grid measurement can take a minute; the default axios timeout is none,
  // but a proxy in front is not — fail loudly instead of hanging the dialog.
  const { data } = await api.post(
    `/searches/${searchId}/businesses/${encodeURIComponent(placeId)}/report/complete`,
    null,
    { timeout: 180000 }
  );
  return data;
}

// Bulk cold-outreach (owner feature, 2026-10-01): step 1 ensures every
// selected business has a working free-report link (skipping ones with no
// email outright) and returns the DataForSEO cost incurred; step 2 sends the
// admin's own template, filled in per business, at a fixed pace.
export async function prepareOutreach(searchId, placeIds) {
  const { data } = await api.post(`/searches/${searchId}/outreach/prepare`, { place_ids: placeIds }, { timeout: 180000 });
  return data;
}

export async function sendOutreach(searchId, { placeIds, subjectTemplate, bodyTemplate, paceSeconds, confirmBulkOutreach }) {
  const { data } = await api.post(`/searches/${searchId}/outreach/send`, {
    place_ids: placeIds,
    subject_template: subjectTemplate,
    body_template: bodyTemplate,
    pace_seconds: paceSeconds,
    confirm_bulk_outreach: confirmBulkOutreach,
  });
  return data;
}

export async function sendReportInterest(token, payload) {
  const { data } = await api.post(`/report/${encodeURIComponent(token)}/interest`, payload || {});
  return data;
}

// "Destek" tab (owner request 2026-10-01): a no-price request for one or
// more additional services, reviewed manually and quoted personally.
export async function sendSupportRequest(token, { name, contact, services, message }) {
  const { data } = await api.post(`/report/${encodeURIComponent(token)}/support-request`, { name, contact, services, message });
  return data;
}

export async function updateStatus(id, placeId, status) {
  const { data } = await api.patch(
    `/searches/${id}/businesses/${encodeURIComponent(placeId)}/status`,
    { status }
  );
  return data;
}

export async function bulkUpdateStatus(id, placeIds, status) {
  const { data } = await api.patch(`/searches/${id}/bulk-status`, {
    place_ids: placeIds,
    status,
  });
  return data;
}

export async function downloadExcel(id, filter, fallbackName) {
  const res = await api.get(`/export/${id}`, {
    params: { filter },
    responseType: "blob",
  });
  const disposition = res.headers["content-disposition"] || "";
  let filename = fallbackName || `leadharita_${filter}.xlsx`;
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (utfMatch) {
    filename = decodeURIComponent(utfMatch[1]);
  } else if (asciiMatch) {
    filename = asciiMatch[1];
  }
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
